import { LivestreamSessionStreamDestination } from 'cb-model';
import { fetchApi, makeResetCache, notifyError, notifyResponse, useCacheQuery } from 'gossamer-front-end';
import { HttpMethod } from 'gossamer-unified';
import React, { useState } from 'react';
import { useDispatch } from 'react-redux';

export const useStreamingDestinations = (livestreamSessionId: string) => {
  const [loadingState, setLoadingState] = useState<'IDLE' | 'LOADING' | 'ERROR'>('IDLE');
  const streamingDestinationsQuery = useCacheQuery(
    LivestreamSessionStreamDestination,
    {
      where: { and: [{ fieldId: 'livestream_session_id', operator: 'eq', value: livestreamSessionId }] },
      limit: 10, // max number of streaming destinations that can be used
    },
    !livestreamSessionId,
  ); // disable query if no session id is provided
  const dispatch = useDispatch();
  const resetCache = makeResetCache(dispatch);

  const callToMainApi = async <T = Object,>(
    method: HttpMethod,
    endpoint: string,
    body?: T,
    successMessage?: string,
  ) => {
    try {
      setLoadingState('LOADING');
      const response = await fetchApi(method, endpoint, body);
      resetCache(LivestreamSessionStreamDestination.id);
      if (successMessage) {
        notifyResponse({ info: successMessage });
      } else {
        notifyResponse(response);
      }
      setLoadingState('IDLE');
      return response;
    } catch (error) {
      setLoadingState('ERROR');
      notifyError(error, 'An error occurred while processing your request.');
    }
  };

  const streamingDestinationsActions = {
    create: async (platform: string, streamKey: string, url: string) => {
      await callToMainApi(
        'POST',
        `/main/livestream-destination-add`,
        {
          livestreamSessionId,
          platform,
          streamKey,
          url,
        },
        'Stream destination added successfully',
      );
    },
    update: async (destinationId: string, platform: string, streamKey: string, url: string) => {
      await callToMainApi(
        'POST',
        `/main/livestream-destination-update`,
        {
          destinationId,
          platform,
          streamKey,
          url,
        },
        'Stream destination updated successfully',
      );
    },
    delete: async (destinationId: string) => {
      await callToMainApi(
        'POST',
        `/main/livestream-destination-delete`,
        { destinationId },
        'Stream destination deleted successfully',
      );
    },
    startAll: async () => {
      await callToMainApi(
        'POST',
        `/main/livestream-destination-start-all`,
        { livestreamSessionId },
        'Stream destinations started successfully',
      );
    },
    stopAll: async () => {
      await callToMainApi(
        'POST',
        `/main/livestream-destination-stop-all`,
        { livestreamSessionId },
        'Stream destinations stopped successfully',
      );
    },
  };

  return {
    loadingState,
    isActionInProgress: loadingState === 'LOADING' || streamingDestinationsQuery?.isFetching,
    streamingDestinations: streamingDestinationsQuery.data || [],
    streamingDestinationsActions,
  };
};
